import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { fmtMoney } from "../components/ops/opsUtils";
import { Bell, AlertTriangle, Clock, Receipt, ListChecks, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import CreateTaskModal from "../components/tasks/CreateTaskModal";
import { toast } from "sonner";

const TYPE_META = {
  aging_threshold: { Icon: Clock, color: "#B45309", bg: "#FEF3C7", label: "Aging threshold" },
  stagnant:        { Icon: AlertTriangle, color: "#B91C1C", bg: "#FEE2E2", label: "Stagnant" },
  payoff_expiring: { Icon: Receipt, color: "#1D4ED8", bg: "#EFF6FF", label: "Payoff expiring" },
};

export default function AlertsView() {
  const [data, setData] = useState({ alerts: [], counts: { total: 0, high: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [taskPrefill, setTaskPrefill] = useState(null);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/operations/alerts`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const filtered = data.alerts.filter((a) => filter === "all" ? true : a.type === filter);

  const counts = {
    aging_threshold: data.alerts.filter((a) => a.type === "aging_threshold").length,
    stagnant: data.alerts.filter((a) => a.type === "stagnant").length,
    payoff_expiring: data.alerts.filter((a) => a.type === "payoff_expiring").length,
  };

  return (
    <div className="p-6 md:p-8 max-w-[1200px] mx-auto" data-testid="alerts-view">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-1">
            <Bell className="w-3.5 h-3.5" /> Alerts
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">Operations alerts</h1>
          <p className="text-sm text-[#6B7280] mt-1">Auto-detected portfolio signals — aging crossings, stagnant cases, and payoffs about to expire.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-4 mb-6">
        <Stat label="Total alerts" value={data.counts.total} accent />
        <Stat label="High severity" value={data.counts.high} warn={data.counts.high > 0} />
        <Stat label="Aging thresholds" value={counts.aging_threshold} />
        <Stat label="Stagnant cases" value={counts.stagnant} />
      </div>

      <div className="flex items-center gap-1 text-xs mb-3">
        {[
          ["all", "All"], ["aging_threshold", "Aging"], ["stagnant", "Stagnant"], ["payoff_expiring", "Payoff expiring"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)}
            data-testid={`alerts-filter-${k}`}
            className={`px-3 py-1.5 rounded-md font-medium transition-colors ${filter === k ? "bg-[#0A2540] text-white" : "text-[#6B7280] hover:bg-white border border-transparent hover:border-[#E5E7EB]"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {loading ? (
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-10 text-center text-[#6B7280] flex items-center justify-center gap-2" data-testid="alerts-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading alerts…
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-16 text-center" data-testid="alerts-empty">
            <div className="inline-flex flex-col items-center text-[#6B7280]">
              <div className="w-12 h-12 rounded-full bg-[#DCFCE7] flex items-center justify-center mb-3"><Bell className="w-5 h-5 text-[#166534]" /></div>
              <div className="text-sm font-medium text-[#0A2540]">All clear</div>
              <div className="text-xs mt-0.5">No alerts under this filter.</div>
            </div>
          </div>
        ) : filtered.map((a) => {
          const meta = TYPE_META[a.type] || TYPE_META.stagnant;
          const Icon = meta.Icon;
          return (
            <div key={a.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex items-start gap-3" data-testid={`alert-${a.id}`}>
              <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: meta.bg }}>
                <Icon className="w-4 h-4" style={{ color: meta.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-2">
                  <div className="text-sm font-medium text-[#0A2540]">{a.title}</div>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-medium" style={{ background: a.severity === "high" ? "#FEE2E2" : "#FEF9C3", color: a.severity === "high" ? "#B91C1C" : "#A16207" }}>{a.severity}</span>
                </div>
                <div className="text-xs text-[#6B7280] mt-0.5">{a.summary}</div>
                <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px]">
                  {a.related_client_name && <Link to={`/admin/clients/${encodeURIComponent(a.related_client_name)}`} className="text-[#0066FF] hover:underline">View client</Link>}
                  {a.related_loan_id && <Link to={`/admin/loans/${a.related_loan_id}`} className="text-[#0066FF] hover:underline">Open loan</Link>}
                  {a.related_payoff_id && <Link to={`/admin/payoffs/${a.related_payoff_id}`} className="text-[#0066FF] hover:underline">View payoff</Link>}
                  <button onClick={() => setTaskPrefill({
                    title: a.title,
                    description: a.summary,
                    related_loan_id: a.related_loan_id || null,
                    related_client_name: a.related_client_name || null,
                    related_payoff_id: a.related_payoff_id || null,
                  })} className="text-[#0066FF] hover:underline" data-testid={`create-task-from-alert-${a.id}`}>
                    Create task →
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <CreateTaskModal open={!!taskPrefill} onClose={() => setTaskPrefill(null)} prefill={taskPrefill || {}} onCreated={() => toast.success("Task created — open /admin/tasks to manage")} />
    </div>
  );
}

function Stat({ label, value, accent, warn }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">{label}</div>
      <div className={`tabular-nums font-display text-2xl font-semibold ${warn ? "text-[#B91C1C]" : accent ? "text-[#0066FF]" : "text-[#0A2540]"}`}>{value}</div>
    </div>
  );
}
